import React, { useContext, useEffect, useState } from "react";

import { ZeosApiContext } from "../../context/ZeosApiContext";
import { IPropertyCoverage, IServiceCoverage } from "../../services/zeos-api/contracts/Meter";

// MTR-P2-03 coverage ledger (task-4051, spec v0.3 coverage table, R8, R10). Per service: what the property declared
// (metering profile, expected count) against what's linked, reporting, pending, missing, excluded and sub-metered,
// plus each service's coverage state and the property's weakest state with the responsible service named.
// Shown at the top of the meters panel, so it has the panel's audience (company Admin / super admin, D3). The API
// refuses it for company Admins on a single-meter property (R1); then nothing renders.

const isSuccess = (status?: string) => status?.toLowerCase() === "success";

const STATE_LABELS: Record<string, string> = {
  COMPLETE: "Complete",
  PARTIAL: "Partial",
  ACTION_NEEDED: "Action needed",
  UNKNOWN: "Unknown",
};

const STATE_TONES: Record<string, string> = {
  COMPLETE: "text-emerald-700",
  PARTIAL: "text-amber-700",
  ACTION_NEEDED: "text-red-700",
  UNKNOWN: "text-slate-600",
};

const PROFILE_LABELS: Record<string, string> = {
  MASTER_METERED: "Owner holds all accounts",
  DIRECT_METERED: "Tenants hold their own",
  NOT_PRESENT: "Not used here",
  UNCONFIRMED: "Not answered yet",
  UNKNOWN: "Not sure",
};

const serviceName = (service: IServiceCoverage) => service.serviceTypeName || service.serviceTypeCode || "Service";

// What the state cell says, including why when it matters.
export const ledgerStateText = (service: IServiceCoverage) => {
  if (service.meteringProfile === "NOT_PRESENT" || !service.coverageState) return "Not used here";
  if (service.isAwaitingAttestation) return "Awaiting attestation";
  const label = STATE_LABELS[service.coverageState] ?? service.coverageState;
  if (service.coverageState === "ACTION_NEEDED") {
    return service.actionNeededReason === "STALLED" ? `${label}: data delayed` : `${label}: meters missing`;
  }
  if (service.coverageState === "COMPLETE" && service.exceedsExpected) return `${label} (more linked than expected)`;
  return label;
};

interface IProps {
  propertyId: number;
  /** Bump to reload, e.g. after a meter is classified or another meter is linked. */
  refreshKey?: number;
}

export const CoverageLedger = ({ propertyId, refreshKey = 0 }: IProps) => {
  const api = useContext(ZeosApiContext);
  const [coverage, setCoverage] = useState<IPropertyCoverage | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadAsync = async () => {
      try {
        const response = await api.Meter.getPropertyCoverageAsync(propertyId);
        if (!cancelled) {
          setCoverage(isSuccess(response.zeosStatus) && response.data ? response.data : null);
        }
      } catch {
        if (!cancelled) setCoverage(null);
      }
    };
    loadAsync();
    return () => {
      cancelled = true;
    };
  }, [api, propertyId, refreshKey]);

  if (!coverage || coverage.services.length === 0) {
    return null;
  }

  const responsible = coverage.services.find((s) => s.serviceTypeCode === coverage.responsibleServiceTypeCode);

  return (
    <section aria-label="Coverage ledger" className="mb-4 border border-gray-200 rounded p-3 bg-gray-50">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <h4 className="text-sm font-semibold text-gray-800">Coverage</h4>
        {coverage.coverageState ? (
          <span className={`text-xs font-medium ${STATE_TONES[coverage.coverageState] ?? "text-slate-600"}`}>
            Property: {STATE_LABELS[coverage.coverageState] ?? coverage.coverageState}
            {responsible ? ` (${serviceName(responsible)})` : ""}
          </span>
        ) : null}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-left text-gray-600">
            <tr>
              <th className="py-1 pr-3">Service</th>
              <th className="py-1 pr-3">Metering</th>
              <th className="py-1 pr-3">Expected</th>
              <th className="py-1 pr-3">Linked</th>
              <th className="py-1 pr-3">Reporting</th>
              <th className="py-1 pr-3">Pending</th>
              <th className="py-1 pr-3">Missing</th>
              <th className="py-1 pr-3">Excluded</th>
              <th className="py-1 pr-3">Sub-meters</th>
              <th className="py-1">State</th>
            </tr>
          </thead>
          <tbody>
            {coverage.services.map((service) => {
              const notUsed = service.meteringProfile === "NOT_PRESENT";
              return (
                <tr key={service.utilityTypeId} className="border-t border-gray-200" data-testid={`ledger-${service.serviceTypeCode}`}>
                  <td className="py-1 pr-3 font-medium">{serviceName(service)}</td>
                  <td className="py-1 pr-3">{PROFILE_LABELS[service.meteringProfile ?? ""] ?? service.meteringProfile ?? "—"}</td>
                  <td className="py-1 pr-3">{notUsed ? "—" : service.expectedMeterCount ?? "Not set"}</td>
                  <td className="py-1 pr-3">{service.linkedMeterCount}</td>
                  <td className="py-1 pr-3">{service.reportingMeterCount}</td>
                  <td className="py-1 pr-3">
                    {service.pendingMeterCount}
                    {service.stalledMeterCount > 0 ? ` (${service.stalledMeterCount} stalled)` : ""}
                  </td>
                  <td className="py-1 pr-3">{notUsed ? "—" : service.missingMeterCount}</td>
                  <td className="py-1 pr-3">{service.excludedMeterCount}</td>
                  <td className="py-1 pr-3">{service.subMeterCount}</td>
                  <td className={`py-1 ${STATE_TONES[service.coverageState ?? ""] ?? "text-slate-600"}`}>{ledgerStateText(service)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
